import type { ValueSetCode } from '../ValueSetCombobox';
import type { PrescribableMedication } from './types';

/** Prefix marking the synthetic "add as free text" dropdown row's `ValueSetCode.code`. The typed
 * name is appended so the row's identity tracks the name — the combobox compares items by `code`,
 * and a name-independent sentinel would paint the row as selected even after the query changed.
 * Can't collide with a real row — routedMedIds are numeric. */
export const FREE_TEXT_CODE_PREFIX = '__free-text__:';

/** Minimum trimmed query length before the ScriptSure search runs and the free-text row shows. */
export const MIN_QUERY_LENGTH = 2;

/** Whether a dropdown item is the synthetic free-text row. */
export function isFreeTextItem(item: ValueSetCode): boolean {
  return item.code.startsWith(FREE_TEXT_CODE_PREFIX);
}

/** The free-text dropdown row for a typed name. */
export function freeTextItem(name: string): ValueSetCode {
  return { code: FREE_TEXT_CODE_PREFIX + name, display: name };
}

/** The typed name carried by a free-text item (or sentinel product `ValueSetCode`). */
export function freeTextName(item: ValueSetCode): string {
  return item.display ?? item.code.slice(FREE_TEXT_CODE_PREFIX.length);
}

/** State of the catalog search backing the dropdown. The free-text row is withheld while the search
 * is unsettled or failed — the spinner and error message render in the combobox's empty state, which
 * any item suppresses, and masking them would invite free-texting a drug the catalog carries. */
export interface DrugSearchState {
  isFetching: boolean;
  isError: boolean;
}

/** The drug-search selection: a catalog drug row (the parent drills it into formulations) or an
 * explicit free-text entry — a medication the catalog doesn't carry (OTCs, supplements), saved to
 * the plan exactly as typed and never prescribable. */
export type DrugSearchSelection = { kind: 'drug'; drug: PrescribableMedication } | { kind: 'freeText'; name: string };

/** Append the synthetic free-text row under the catalog results once the query is searchable and
 * the search has settled without error, so a medication ScriptSure doesn't carry can still be added
 * as typed. The row's (trimmed) query becomes the free-text name on selection. */
export function withFreeTextItem(drugRows: ValueSetCode[], query: string, search: DrugSearchState): ValueSetCode[] {
  const name = query.trim();
  if (name.length < MIN_QUERY_LENGTH || search.isFetching || search.isError) return drugRows;
  return [...drugRows, freeTextItem(name)];
}

/** The two shapes of a drug-search selection, split for consumers: the catalog drug (drives the
 * formulation lookups) and the free-text name (drives manual entry directly). */
export function splitSelection(selection: DrugSearchSelection | null): {
  selectedDrug: PrescribableMedication | null;
  freeTextName: string | null;
} {
  return {
    selectedDrug: selection?.kind === 'drug' ? selection.drug : null,
    freeTextName: selection?.kind === 'freeText' ? selection.name : null,
  };
}

/** Map a combobox pick back to a selection: a free-text entry for the sentinel row, else the
 * matching catalog row (reconstructed from the pick when the search results have moved on). */
export function toDrugSearchSelection(
  selected: ValueSetCode | null,
  rows: PrescribableMedication[],
): DrugSearchSelection | null {
  if (!selected) return null;
  if (isFreeTextItem(selected)) {
    return { kind: 'freeText', name: freeTextName(selected) };
  }
  const match = rows.find((m) => m.routedMedId === selected.code);
  return {
    kind: 'drug',
    drug: match ?? { name: selected.display ?? selected.code, routedMedId: selected.code },
  };
}
